import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, Users, Search, ChevronDown, ChevronUp,
  BookOpen, Clock, Eye, X, Star, Trophy,
  Flame, Target, Settings, ChevronLeft, ChevronRight,
  Zap, BarChart3, Library, ArrowRight, Mail,
  Calendar, GraduationCap, ArrowLeft, Layers, FolderOpen
} from '../DeskeyIcons';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token');
  const res = await fetch(`${API}/api${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...opts.headers } });
  return res.json();
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '--';
const fmtDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '--';

const TEACHER_COLORS = ['#34A853','#4285F4','#EA4335','#FBBC04','#673AB7','#FF5722','#009688','#3F51B5','#E91E63','#00BCD4'];
const getInitialColor = (name, i) => TEACHER_COLORS[i % TEACHER_COLORS.length];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN EXPORT — Teacher Desk: Teacher List View
═══════════════════════════════════════════════════════════════════════════ */
export function TeacherDeskSection({ accent, isDepartment = false }) {
  const A = accent || '#34A853';
  const prefix = isDepartment ? '/department' : '/institution';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api(`${prefix}/teacher-desk/teachers-summary`);
      setData(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [prefix]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data?.teachers) return [];
    if (!search.trim()) return data.teachers;
    const q = search.toLowerCase();
    return data.teachers.filter(t =>
      t.full_name.toLowerCase().includes(q) ||
      t.email.toLowerCase().includes(q) ||
      (t.department_name || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-slate-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="teacher-desk-section">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-shrink-0">
        <div>
          <h2 className="text-lg font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Teacher Desk</h2>
          <p className="text-[11px] text-gray-500">Monitor teacher activity, uploads & incentive points</p>
        </div>
        <div className="flex items-center gap-2">
          {!isDepartment && (
            <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} className="text-xs gap-1.5 h-8 border-gray-200" data-testid="btn-incentive-settings">
              <Settings className="w-3.5 h-3.5" /> Incentive Settings
            </Button>
          )}
        </div>
      </div>

      {/* Summary Stats Row */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3 flex-shrink-0" data-testid="teacher-desk-stats">
          {[
            { label: 'Teachers', val: data.total_teachers, icon: Users, color: A, sub: 'Active teachers' },
            { label: 'Total Uploads', val: data.total_uploads, icon: FileText, color: '#3b82f6', sub: 'Published lessons' },
            { label: 'Avg On-Time', val: `${data.avg_on_time_pct}%`, icon: Target, color: data.avg_on_time_pct >= 60 ? '#10b981' : '#f59e0b', sub: 'Upload compliance' },
            { label: 'Deadline', val: `${String(data.settings?.upload_deadline_hour ?? 17).padStart(2, '0')}:${String(data.settings?.upload_deadline_minute ?? 0).padStart(2, '0')}`, icon: Clock, color: '#8b5cf6', sub: `${data.settings?.points_on_time || 10} pts on-time` },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-xl shadow p-3 flex items-center gap-3 transition-all hover:shadow-md hover:-translate-y-0.5 group" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105" style={{ background: `${s.color}12` }}>
                <s.icon className="w-4 h-4" style={{ color: s.color }} strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-extrabold text-gray-900 tabular-nums leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
                <p className="text-[10px] text-gray-500 truncate">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search + Teacher List */}
      <div className="bg-white border border-gray-200 rounded-xl shadow flex-1 min-h-0 flex flex-col overflow-hidden" data-testid="teacher-desk-list-container">
        {/* Search Bar */}
        <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-shrink-0">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search teachers..."
              className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white placeholder:text-gray-300"
              data-testid="td-search"
            />
          </div>
          <span className="text-[10px] text-gray-500 tabular-nums ml-auto">{filtered.length} teacher{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Teacher Cards */}
        <div className="flex-1 overflow-y-auto p-3" data-testid="teacher-card-list">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-10">
              <Users className="w-14 h-14 text-gray-200 mb-3" />
              <p className="text-sm text-gray-500 font-medium">No teachers found</p>
              <p className="text-[11px] text-gray-300 mt-1">Teachers will appear here once added to the institution</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {filtered.map((t, idx) => (
                <TeacherCard key={t.id} teacher={t} idx={idx} A={A} onClick={() => setSelectedTeacher(t)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Teacher Detail Popup */}
      {selectedTeacher && (
        <TeacherDetailModal
          A={A}
          teacher={selectedTeacher}
          prefix={prefix}
          onClose={() => setSelectedTeacher(null)}
        />
      )}

      {/* Incentive Settings Modal */}
      {showSettings && (
        <IncentiveSettingsModal
          A={A}
          settings={data?.settings}
          onClose={() => setShowSettings(false)}
          onSave={async (formData) => {
            await api(`/institution/teacher-desk/incentive-settings`, { method: 'PUT', body: JSON.stringify(formData) });
            setShowSettings(false);
            load();
          }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TEACHER CARD — Grid Item
═══════════════════════════════════════════════════════════════════════════ */
function TeacherCard({ teacher: t, idx, A, onClick }) {
  const color = getInitialColor(t.full_name, idx);
  const hasUploads = t.total_uploads > 0;

  return (
    <button
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-xl p-3.5 text-left transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300 group w-full"
      data-testid={`teacher-card-${t.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 shadow" style={{ background: color }}>
          {t.full_name?.charAt(0)?.toUpperCase() || '?'}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-gray-900 truncate group-hover:text-gray-800">{t.full_name}</h3>
            <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-600 flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
          </div>
          <p className="text-[10px] text-gray-500 truncate">{t.email}</p>
          {t.department_name && <p className="text-[10px] text-gray-500 mt-0.5">{t.department_name}</p>}
        </div>
      </div>

      {/* Stats Row */}
      <div className="mt-3 pt-2.5 border-t border-gray-100 grid grid-cols-3 gap-2">
        <div className="text-center">
          <p className="text-sm font-extrabold text-gray-900 tabular-nums">{t.total_uploads}</p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">Uploads</p>
        </div>
        <div className="text-center">
          <p className={`text-sm font-extrabold tabular-nums ${t.on_time_pct >= 60 ? 'text-emerald-600' : t.on_time_pct >= 30 ? 'text-amber-600' : 'text-gray-500'}`}>
            {hasUploads ? `${t.on_time_pct}%` : '--'}
          </p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">On-Time</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-gray-600 tabular-nums">{t.last_upload ? fmtDate(t.last_upload) : '--'}</p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">Last Upload</p>
        </div>
      </div>

      {/* On-time bar */}
      {hasUploads && (
        <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${t.on_time_pct}%`, background: t.on_time_pct >= 60 ? '#10b981' : t.on_time_pct >= 30 ? '#f59e0b' : '#ef4444' }} />
        </div>
      )}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TEACHER DETAIL MODAL — Big Popup
═══════════════════════════════════════════════════════════════════════════ */
function TeacherDetailModal({ A, teacher, prefix, onClose }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeView, setActiveView] = useState('classes'); // 'classes' | 'log' | 'library'
  const [expandedId, setExpandedId] = useState(null);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${prefix}/teacher-desk/teacher/${teacher.id}/lessons`;
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (params.length) url += `?${params.join('&')}`;
      const d = await api(url);
      setDetail(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [prefix, teacher.id, dateFrom, dateTo]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const clearDates = () => { setDateFrom(''); setDateTo(''); };
  const lessons = detail?.lessons || [];
  const stats = detail?.stats || {};
  const teacherInfo = detail?.teacher || teacher;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="teacher-detail-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[92vw] max-w-[1100px] h-[88vh] flex flex-col overflow-hidden">

        {/* Modal Header — Teacher Info */}
        <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0" style={{ background: `linear-gradient(135deg, ${A}06, transparent)` }}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white shadow-md" style={{ background: A }}>
                {teacherInfo.full_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{teacherInfo.full_name}</h2>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" /> {teacherInfo.email}</span>
                  {teacherInfo.department_name && <span className="text-xs text-gray-500">{teacherInfo.department_name}</span>}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" data-testid="teacher-detail-close">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Quick Stats */}
          {!loading && (
            <div className="flex items-center gap-4 mt-3 flex-wrap" data-testid="teacher-detail-stats">
              {[
                { label: 'Total Uploads', val: lessons.length, icon: FileText, color: '#3b82f6' },
                { label: 'On-Time', val: stats.on_time_count || 0, icon: Zap, color: '#10b981' },
                { label: 'Late', val: stats.late_count || 0, icon: Clock, color: '#f59e0b' },
                { label: 'On-Time Rate', val: `${stats.on_time_pct || 0}%`, icon: Target, color: stats.on_time_pct >= 60 ? '#10b981' : '#f59e0b' },
                { label: 'Points', val: stats.total_points || 0, icon: Star, color: '#f59e0b' },
                { label: 'Streak', val: `${stats.current_streak || 0} days`, icon: Flame, color: '#ef4444' },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-lg border border-gray-100 shadow">
                  <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                  <span className="text-xs font-bold text-gray-800 tabular-nums">{s.val}</span>
                  <span className="text-[9px] text-gray-500">{s.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Toolbar: View Toggle + Date Filter */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-3 flex-shrink-0 flex-wrap">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
            {[
              { id: 'classes', label: 'Classes & Chapters', icon: BookOpen },
              { id: 'log', label: 'Activity Log', icon: BarChart3 },
              { id: 'library', label: 'Lecture Library', icon: Library },
            ].map(v => (
              <button
                key={v.id}
                onClick={() => setActiveView(v.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all ${
                  activeView === v.id ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'
                }`}
                data-testid={`view-${v.id}`}
              >
                <v.icon className="w-3.5 h-3.5" /> {v.label}
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-gray-200" />

          {/* Date Filters — only for lesson-based views */}
          {activeView !== 'classes' && (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-500 font-medium">From</span>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-[11px] rounded-md border border-gray-200 px-2 bg-white text-gray-700" data-testid="detail-date-from" />
                <span className="text-[10px] text-gray-500 font-medium">To</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-[11px] rounded-md border border-gray-200 px-2 bg-white text-gray-700" data-testid="detail-date-to" />
                {(dateFrom || dateTo) && (
                  <button onClick={clearDates} className="text-[10px] text-gray-500 hover:text-gray-700 flex items-center gap-0.5" data-testid="detail-clear-dates">
                    <X className="w-3 h-3" /> Clear
                  </button>
                )}
              </div>

              <span className="text-[10px] text-gray-500 tabular-nums ml-auto">{lessons.length} record{lessons.length !== 1 ? 's' : ''}</span>
            </>
          )}
          {activeView === 'classes' && (
            <span className="text-[10px] text-gray-500 ml-auto flex items-center gap-1">
              <Eye className="w-3 h-3" /> Read-only portfolio view
            </span>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-7 h-7 border-2 border-gray-200 border-t-slate-500 rounded-full animate-spin" />
            </div>
          ) : activeView === 'classes' ? (
            <ClassesPortfolioView A={A} teacherId={teacher.id} />
          ) : lessons.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-10">
              <FileText className="w-14 h-14 text-gray-200 mb-3" />
              <p className="text-sm text-gray-500 font-medium">No lessons found</p>
              <p className="text-[11px] text-gray-300 mt-1">{(dateFrom || dateTo) ? 'Try adjusting the date range' : 'This teacher hasn\'t published any lessons yet'}</p>
            </div>
          ) : activeView === 'log' ? (
            <ActivityLogView A={A} lessons={lessons} />
          ) : (
            <LibraryView A={A} lessons={lessons} expandedId={expandedId} setExpandedId={setExpandedId} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ACTIVITY LOG VIEW — Timeline of uploads
═══════════════════════════════════════════════════════════════════════════ */
function ActivityLogView({ A, lessons }) {
  // Group by date
  const grouped = useMemo(() => {
    const map = {};
    for (const ls of lessons) {
      const dateKey = ls.published_at ? new Date(ls.published_at).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' }) : 'Unknown Date';
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(ls);
    }
    return Object.entries(map);
  }, [lessons]);

  return (
    <div className="p-5 space-y-4" data-testid="activity-log-view">
      {grouped.map(([date, items]) => (
        <div key={date}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full" style={{ background: A }} />
            <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">{date}</h4>
            <div className="flex-1 h-px bg-gray-100" />
            <span className="text-[10px] text-gray-500 tabular-nums">{items.length} upload{items.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="ml-3.5 pl-4 border-l-2 border-gray-100 space-y-2">
            {items.map(ls => (
              <div key={ls.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors group" data-testid={`log-item-${ls.id}`}>
                {/* Time + Status indicator */}
                <div className="flex items-center gap-2 flex-shrink-0 w-[90px]">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ls.is_on_time ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                  <span className="text-[11px] text-gray-600 tabular-nums font-medium">{fmtTime(ls.published_at)}</span>
                </div>

                {/* Lesson info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{ls.title}</p>
                  {ls.summary && <p className="text-[10px] text-gray-500 truncate mt-0.5">{ls.summary}</p>}
                </div>

                {/* Status badge */}
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${ls.is_on_time ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                  {ls.is_on_time ? 'On-time' : 'Late'}
                </span>

                {/* Views */}
                <div className="flex items-center gap-1 flex-shrink-0 w-[50px] justify-end">
                  <Eye className="w-3 h-3 text-gray-300" />
                  <span className="text-[10px] text-gray-500 tabular-nums">{ls.views}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LIBRARY VIEW — Full Lecture Cards
═══════════════════════════════════════════════════════════════════════════ */
function LibraryView({ A, lessons, expandedId, setExpandedId }) {
  return (
    <div className="p-5 space-y-2" data-testid="library-view">
      {lessons.map(ls => {
        const isOpen = expandedId === ls.id;
        return (
          <div key={ls.id} className={`border rounded-xl transition-all duration-200 ${isOpen ? 'border-gray-300 shadow-md' : 'border-gray-200 hover:border-gray-200 hover:shadow'}`} data-testid={`library-item-${ls.id}`}>
            {/* Row Header */}
            <button className="w-full flex items-center gap-3 px-4 py-3 text-left" onClick={() => setExpandedId(isOpen ? null : ls.id)}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${A}10` }}>
                <BookOpen className="w-4 h-4" style={{ color: A }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">{ls.title}</p>
                <p className="text-[10px] text-gray-500">{fmtDateTime(ls.published_at)}</p>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${ls.is_on_time ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                {ls.is_on_time ? 'On-time' : 'Late'}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Eye className="w-3 h-3 text-gray-300" />
                <span className="text-[10px] text-gray-500 tabular-nums">{ls.views}</span>
              </div>
              {isOpen ? <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-300 flex-shrink-0" />}
            </button>

            {/* Expanded Content */}
            {isOpen && (
              <div className="px-4 pb-4 pt-0 border-t border-gray-100 mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
                  {ls.summary && (
                    <div className="md:col-span-2">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Summary</p>
                      <p className="text-sm text-gray-800 leading-relaxed">{ls.summary}</p>
                    </div>
                  )}
                  {(ls.key_concepts || []).length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Key Concepts</p>
                      <div className="flex flex-wrap gap-1.5">
                        {ls.key_concepts.map((c, i) => (
                          <span key={i} className="text-[11px] px-2 py-0.5 rounded-md font-medium border" style={{ background: `${A}08`, borderColor: `${A}20`, color: A }}>{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(ls.bullet_notes || []).length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Notes</p>
                      <ul className="space-y-1">
                        {ls.bullet_notes.map((n, i) => (
                          <li key={i} className="text-[11px] text-gray-700 flex items-start gap-1.5">
                            <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: A }} />{n}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {ls.homework && (
                    <div className="md:col-span-2">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Homework</p>
                      <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        <p className="text-xs text-amber-800">{ls.homework}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   INCENTIVE SETTINGS MODAL
═══════════════════════════════════════════════════════════════════════════ */
function IncentiveSettingsModal({ A, settings, onClose, onSave }) {
  const [form, setForm] = useState({
    upload_deadline_hour: settings?.upload_deadline_hour ?? 17,
    upload_deadline_minute: settings?.upload_deadline_minute ?? 0,
    points_on_time: settings?.points_on_time ?? 10,
    points_late: settings?.points_late ?? 3,
    streak_bonus_multiplier: settings?.streak_bonus_multiplier ?? 1.5,
    enabled: settings?.enabled ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => { setSaving(true); await onSave(form); setSaving(false); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="incentive-settings-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[420px] max-h-[80vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4" style={{ color: A }} />
            <h3 className="text-sm font-bold text-gray-900">Incentive Settings</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center" data-testid="settings-close"><X className="w-4 h-4 text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-800">Enable Incentive System</label>
            <button onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))} className={`w-10 h-5 rounded-full transition-colors relative ${form.enabled ? '' : 'bg-gray-200'}`} style={form.enabled ? { background: A } : {}} data-testid="settings-toggle-enabled">
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.enabled ? 'left-5' : 'left-0.5'}`} />
            </button>
          </div>
          {/* Deadline */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Daily Upload Deadline</label>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="number" min={0} max={23} value={form.upload_deadline_hour} onChange={e => setForm(f => ({ ...f, upload_deadline_hour: parseInt(e.target.value) || 0 }))} className="w-16 h-9 text-center text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300" data-testid="settings-deadline-hour" />
              <span className="text-lg font-bold text-gray-500">:</span>
              <input type="number" min={0} max={59} value={form.upload_deadline_minute} onChange={e => setForm(f => ({ ...f, upload_deadline_minute: parseInt(e.target.value) || 0 }))} className="w-16 h-9 text-center text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300" data-testid="settings-deadline-minute" />
              <span className="text-xs text-gray-500 ml-2">
                ({form.upload_deadline_hour >= 12 ? `${form.upload_deadline_hour === 12 ? 12 : form.upload_deadline_hour - 12}:${String(form.upload_deadline_minute).padStart(2, '0')} PM` : `${form.upload_deadline_hour === 0 ? 12 : form.upload_deadline_hour}:${String(form.upload_deadline_minute).padStart(2, '0')} AM`})
              </span>
            </div>
          </div>
          {/* Points */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Points (On-Time)</label>
              <input type="number" min={1} max={100} value={form.points_on_time} onChange={e => setForm(f => ({ ...f, points_on_time: parseInt(e.target.value) || 1 }))} className="w-full h-9 text-sm rounded-lg border border-gray-200 px-3 mt-1.5 focus:outline-none focus:ring-1 focus:ring-gray-300" data-testid="settings-pts-ontime" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Points (Late)</label>
              <input type="number" min={0} max={50} value={form.points_late} onChange={e => setForm(f => ({ ...f, points_late: parseInt(e.target.value) || 0 }))} className="w-full h-9 text-sm rounded-lg border border-gray-200 px-3 mt-1.5 focus:outline-none focus:ring-1 focus:ring-gray-300" data-testid="settings-pts-late" />
            </div>
          </div>
          {/* Streak */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Streak Bonus (3+ on-time days)</label>
            <input type="number" min={1} max={5} step={0.1} value={form.streak_bonus_multiplier} onChange={e => setForm(f => ({ ...f, streak_bonus_multiplier: parseFloat(e.target.value) || 1 }))} className="w-24 h-9 text-sm rounded-lg border border-gray-200 px-3 mt-1.5 focus:outline-none focus:ring-1 focus:ring-gray-300" data-testid="settings-streak-mult" />
            <p className="text-[10px] text-gray-500 mt-1">{form.points_on_time} x {form.streak_bonus_multiplier} = {Math.round(form.points_on_time * form.streak_bonus_multiplier)} pts during streak</p>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="settings-cancel">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} style={{ background: A }} className="text-white" data-testid="settings-save">{saving ? 'Saving...' : 'Save Settings'}</Button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   CLASSES PORTFOLIO VIEW — Read-only drill-down of teacher's classes →
   chapters → topics, mirroring the teacher's own "My Classes" UX but
   with NO edit/add/delete affordances (admin view only).
═══════════════════════════════════════════════════════════════════════════ */
function ClassesPortfolioView({ A, teacherId }) {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [selectedClass, setSelectedClass] = useState(null); // {id,name,...}
  const [chapters, setChapters] = useState(null);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [classSearch, setClassSearch] = useState('');
  const [chapterSearch, setChapterSearch] = useState('');

  const [selectedChapter, setSelectedChapter] = useState(null);
  const [topics, setTopics] = useState(null);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicSearch, setTopicSearch] = useState('');

  const [previewTopic, setPreviewTopic] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Load portfolio on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr('');
      try {
        const d = await api(`/institution/teachers/${teacherId}/portfolio`);
        if (cancelled) return;
        if (d?.detail) { setErr(d.detail); setPortfolio(null); }
        else setPortfolio(d);
      } catch (e) {
        if (!cancelled) setErr('Failed to load portfolio');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [teacherId]);

  // Load chapters when a class is selected
  useEffect(() => {
    if (!selectedClass) { setChapters(null); return; }
    let cancelled = false;
    (async () => {
      setChaptersLoading(true);
      try {
        const d = await api(`/institution/teachers/${teacherId}/classes/${selectedClass.id}/chapters`);
        if (cancelled) return;
        setChapters(d?.chapters || []);
      } catch (e) {
        if (!cancelled) setChapters([]);
      }
      if (!cancelled) setChaptersLoading(false);
    })();
    return () => { cancelled = true; };
  }, [teacherId, selectedClass]);

  // Load topics when a chapter is selected
  useEffect(() => {
    if (!selectedChapter) { setTopics(null); return; }
    let cancelled = false;
    (async () => {
      setTopicsLoading(true);
      try {
        const d = await api(`/institution/teachers/${teacherId}/chapters/${selectedChapter.id}/topics`);
        if (cancelled) return;
        setTopics(d?.topics || []);
      } catch (e) {
        if (!cancelled) setTopics([]);
      }
      if (!cancelled) setTopicsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [teacherId, selectedChapter]);

  const openTopicPreview = async (topic) => {
    setPreviewLoading(true);
    setPreviewTopic({ ...topic, framework: null, _loading: true });
    try {
      const d = await api(`/institution/teachers/${teacherId}/topics/${topic.id}`);
      setPreviewTopic(d?.topic || { ...topic, framework: null });
    } catch (e) {
      setPreviewTopic({ ...topic, framework: null });
    }
    setPreviewLoading(false);
  };

  const filteredClasses = useMemo(() => {
    const all = portfolio?.classes || [];
    if (!classSearch.trim()) return all;
    const q = classSearch.toLowerCase();
    return all.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.class_code || '').toLowerCase().includes(q) ||
      (c.subjects || []).some(s => (s || '').toLowerCase().includes(q)) ||
      (c.department_name || '').toLowerCase().includes(q) ||
      (c.batch_name || '').toLowerCase().includes(q)
    );
  }, [portfolio, classSearch]);

  const filteredChapters = useMemo(() => {
    const all = chapters || [];
    if (!chapterSearch.trim()) return all;
    const q = chapterSearch.toLowerCase();
    return all.filter(ch =>
      (ch.title || '').toLowerCase().includes(q) ||
      (ch.description || '').toLowerCase().includes(q)
    );
  }, [chapters, chapterSearch]);

  const filteredTopics = useMemo(() => {
    const all = topics || [];
    if (!topicSearch.trim()) return all;
    const q = topicSearch.toLowerCase();
    return all.filter(t =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.summary || '').toLowerCase().includes(q)
    );
  }, [topics, topicSearch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-16">
        <div className="w-7 h-7 border-2 border-gray-200 border-t-slate-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center py-10" data-testid="portfolio-error">
        <BookOpen className="w-14 h-14 text-gray-200 mb-3" />
        <p className="text-sm text-gray-500 font-medium">{err}</p>
      </div>
    );
  }

  if (!portfolio || !portfolio.classes || portfolio.classes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center py-14" data-testid="portfolio-empty">
        <BookOpen className="w-14 h-14 text-gray-200 mb-3" />
        <p className="text-sm text-gray-500 font-medium">No classes assigned to this teacher</p>
        <p className="text-[11px] text-gray-300 mt-1">When a class is assigned, the teacher's chapters and topics will appear here.</p>
      </div>
    );
  }

  /* ── Render breadcrumbs + current level ───────────────────────────────── */
  return (
    <div className="p-5 space-y-4" data-testid="portfolio-view">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-1.5 text-[11px] text-gray-500 flex-wrap" data-testid="portfolio-breadcrumbs">
        <button
          onClick={() => { setSelectedClass(null); setSelectedChapter(null); setChapterSearch(''); setTopicSearch(''); }}
          className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${!selectedClass ? 'font-bold text-gray-900 bg-gray-100' : 'hover:bg-gray-100 hover:text-gray-700'}`}
          data-testid="crumb-classes"
        >
          <GraduationCap className="w-3 h-3" /> Classes
        </button>
        {selectedClass && (
          <>
            <ChevronRight className="w-3 h-3 text-gray-300" />
            <button
              onClick={() => { setSelectedChapter(null); setTopicSearch(''); }}
              className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${!selectedChapter ? 'font-bold text-gray-900 bg-gray-100' : 'hover:bg-gray-100 hover:text-gray-700'}`}
              data-testid="crumb-class"
            >
              <BookOpen className="w-3 h-3" /> {selectedClass.name}
            </button>
          </>
        )}
        {selectedChapter && (
          <>
            <ChevronRight className="w-3 h-3 text-gray-300" />
            <span className="flex items-center gap-1 px-2 py-1 rounded-md font-bold text-gray-900 bg-gray-100" data-testid="crumb-chapter">
              <FolderOpen className="w-3 h-3" /> {selectedChapter.title}
            </span>
          </>
        )}
      </div>

      {/* Level 1 — Classes Grid */}
      {!selectedClass && (
        <div data-testid="portfolio-classes-level">
          {/* Portfolio Stats Strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="portfolio-stats">
            {[
              { label: 'Classes', val: portfolio.stats?.class_count || 0, icon: GraduationCap, color: A },
              { label: 'Chapters', val: portfolio.stats?.chapter_count || 0, icon: BookOpen, color: '#3b82f6' },
              { label: 'Topics', val: portfolio.stats?.topic_count || 0, icon: Layers, color: '#8b5cf6' },
              { label: 'Students', val: portfolio.stats?.student_count || 0, icon: Users, color: '#f59e0b' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-2.5 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${s.color}12` }}>
                  <s.icon className="w-4 h-4" style={{ color: s.color }} />
                </div>
                <div>
                  <p className="text-base font-extrabold text-gray-900 tabular-nums leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
                  <p className="text-[9px] uppercase tracking-wider text-gray-500">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-3 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              value={classSearch}
              onChange={e => setClassSearch(e.target.value)}
              placeholder="Search classes, subjects, departments..."
              className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
              data-testid="portfolio-class-search"
            />
          </div>

          {filteredClasses.length === 0 ? (
            <p className="text-[12px] text-gray-400 text-center py-8">No classes match your search.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {filteredClasses.map(cls => (
                <PortfolioClassCard key={cls.id} A={A} cls={cls} onOpen={() => setSelectedClass(cls)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Level 2 — Chapters List for selected class */}
      {selectedClass && !selectedChapter && (
        <div data-testid="portfolio-chapters-level">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <button
              onClick={() => { setSelectedClass(null); setChapters(null); setChapterSearch(''); }}
              className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 hover:border-gray-300 rounded-md px-2.5 py-1.5 transition-colors"
              data-testid="portfolio-back-to-classes"
            >
              <ArrowLeft className="w-3 h-3" /> Back to Classes
            </button>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                value={chapterSearch}
                onChange={e => setChapterSearch(e.target.value)}
                placeholder="Search chapters..."
                className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                data-testid="portfolio-chapter-search"
              />
            </div>
          </div>

          {/* Class header card */}
          <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-[#34A853]/5 to-transparent p-4 mb-4" data-testid="portfolio-class-header">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow" style={{ background: A }}>
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-[15px] font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedClass.name}</h3>
                  {selectedClass.class_code && <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{selectedClass.class_code}</span>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-600 flex-wrap">
                  {(selectedClass.subjects || []).length > 0 && (
                    <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {(selectedClass.subjects || []).join(', ')}</span>
                  )}
                  {selectedClass.department_name && <span>{selectedClass.department_name}</span>}
                  {selectedClass.batch_name && <span>· {selectedClass.batch_name}</span>}
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {selectedClass.student_count} students</span>
                  <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {selectedClass.chapter_count} chapters</span>
                  <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {selectedClass.topic_count} topics</span>
                </div>
              </div>
            </div>
          </div>

          {chaptersLoading ? (
            <div className="flex items-center justify-center py-10"><div className="w-6 h-6 border-2 border-gray-200 border-t-slate-500 rounded-full animate-spin" /></div>
          ) : filteredChapters.length === 0 ? (
            <div className="text-center py-10">
              <BookOpen className="w-12 h-12 text-gray-200 mx-auto mb-2" />
              <p className="text-[13px] text-gray-500 font-medium">{chapterSearch ? 'No chapters match your search' : 'No chapters created yet'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredChapters.map(ch => (
                <PortfolioChapterRow key={ch.id} A={A} chapter={ch} onOpen={() => setSelectedChapter(ch)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Level 3 — Topics list for selected chapter */}
      {selectedChapter && (
        <div data-testid="portfolio-topics-level">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <button
              onClick={() => { setSelectedChapter(null); setTopics(null); setTopicSearch(''); }}
              className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 hover:border-gray-300 rounded-md px-2.5 py-1.5 transition-colors"
              data-testid="portfolio-back-to-chapters"
            >
              <ArrowLeft className="w-3 h-3" /> Back to Chapters
            </button>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                value={topicSearch}
                onChange={e => setTopicSearch(e.target.value)}
                placeholder="Search topics..."
                className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                data-testid="portfolio-topics-search"
              />
            </div>
          </div>

          {/* Chapter header card */}
          <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-[#0d9488]/5 to-transparent p-4 mb-4" data-testid="portfolio-chapter-header">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#34A853] to-[#0d9488] shadow">
                <FolderOpen className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#0d9488] mb-0.5">Chapter</p>
                <h3 className="text-[15px] font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedChapter.title}</h3>
                {selectedChapter.description && <p className="text-[11px] text-gray-600 mt-1 line-clamp-2">{selectedChapter.description}</p>}
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-600">
                  <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {selectedChapter.topic_count ?? (topics?.length || 0)} topics</span>
                  {selectedChapter.latest_topic_date && (
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Latest {fmtDate(selectedChapter.latest_topic_date)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {topicsLoading ? (
            <div className="flex items-center justify-center py-10"><div className="w-6 h-6 border-2 border-gray-200 border-t-slate-500 rounded-full animate-spin" /></div>
          ) : filteredTopics.length === 0 ? (
            <div className="text-center py-10">
              <Layers className="w-12 h-12 text-gray-200 mx-auto mb-2" />
              <p className="text-[13px] text-gray-500 font-medium">{topicSearch ? 'No topics match your search' : 'No topics published yet in this chapter'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTopics.map(t => (
                <PortfolioTopicRow key={t.id} A={A} topic={t} onView={() => openTopicPreview(t)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Topic Preview (read-only) */}
      {previewTopic && (
        <PortfolioTopicPreview A={A} topic={previewTopic} loading={previewLoading} onClose={() => setPreviewTopic(null)} />
      )}
    </div>
  );
}

/* Class card inside the portfolio (clickable) */
function PortfolioClassCard({ A, cls, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="bg-white border border-gray-200 rounded-xl p-3.5 text-left transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:border-[#34A853]/40 group w-full"
      data-testid={`portfolio-class-${cls.id}`}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `${A}15` }}>
          <GraduationCap className="w-4.5 h-4.5" style={{ color: A, width: 18, height: 18 }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1.5">
            <h3 className="text-[13.5px] font-extrabold text-gray-900 truncate group-hover:text-[#0d9488]" style={{ fontFamily: 'Manrope, sans-serif' }}>{cls.name}</h3>
            <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#34A853] flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
          </div>
          {(cls.subjects || []).length > 0 && (
            <p className="text-[10px] text-gray-500 truncate">{(cls.subjects || []).join(' · ')}</p>
          )}
          {cls.class_code && (
            <p className="text-[9px] text-gray-400 mt-0.5 tabular-nums">{cls.class_code}</p>
          )}
        </div>
      </div>
      <div className="mt-3 pt-2.5 border-t border-gray-100 grid grid-cols-3 gap-2">
        <div className="text-center">
          <p className="text-sm font-extrabold text-gray-900 tabular-nums">{cls.chapter_count}</p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">Chapters</p>
        </div>
        <div className="text-center">
          <p className="text-sm font-extrabold text-gray-900 tabular-nums">{cls.topic_count}</p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">Topics</p>
        </div>
        <div className="text-center">
          <p className="text-sm font-extrabold text-gray-900 tabular-nums">{cls.student_count}</p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">Students</p>
        </div>
      </div>
      {(cls.department_name || cls.batch_name) && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {cls.department_name && <span className="text-[9px] font-semibold bg-gray-50 text-gray-600 px-1.5 py-0.5 rounded">{cls.department_name}</span>}
          {cls.batch_name && <span className="text-[9px] font-semibold bg-gray-50 text-gray-600 px-1.5 py-0.5 rounded">{cls.batch_name}</span>}
        </div>
      )}
    </button>
  );
}

/* Chapter row inside the portfolio (clickable, no edit/delete) */
function PortfolioChapterRow({ A, chapter, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="w-full flex items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3.5 text-left hover:border-[#34A853]/40 hover:shadow-sm transition-all group"
      data-testid={`portfolio-chapter-${chapter.id}`}
    >
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#34A853]/10 to-[#06B6D4]/10 border border-[#34A853]/20 flex items-center justify-center flex-shrink-0">
        <BookOpen className="w-5 h-5 text-[#0d9488]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <p className="text-[14px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{chapter.title}</p>
          {chapter.status === 'published' && <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Published</span>}
        </div>
        {chapter.description && <p className="text-[11px] text-gray-600 line-clamp-2 leading-relaxed">{chapter.description}</p>}
        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {chapter.topic_count || 0} topics</span>
          {chapter.latest_topic_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Latest {fmtDate(chapter.latest_topic_date)}</span>}
          {chapter.created_at && <span>Created {fmtDate(chapter.created_at)}</span>}
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#34A853] flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

/* Topic row inside the portfolio (read-only View only) */
function PortfolioTopicRow({ A, topic, onView }) {
  const date = topic.topic_date ? new Date(topic.topic_date) : null;
  const subtitle = (topic.summary || '').trim().slice(0, 160);
  return (
    <div className="flex items-stretch gap-3 rounded-xl border border-zinc-200 bg-white p-3.5 hover:border-[#34A853]/40 hover:shadow-sm transition-all group" data-testid={`portfolio-topic-${topic.id}`}>
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#34A853]/10 to-[#06B6D4]/10 border border-[#34A853]/20 flex flex-col items-center justify-center flex-shrink-0">
        <span className="text-[9px] font-bold uppercase tracking-wider text-[#0d9488]">{date ? date.toLocaleString('en-US', { month: 'short' }) : '—'}</span>
        <span className="text-[18px] font-extrabold text-[#0d9488] leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>{date ? date.getDate() : '--'}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <p className="text-[14px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{topic.title}</p>
          {topic.status === 'published' && <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Published</span>}
        </div>
        {subtitle && <p className="text-[11px] text-gray-600 line-clamp-2 leading-relaxed">{subtitle}</p>}
        {(topic.key_topics || []).length > 0 && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {(topic.key_topics || []).slice(0, 4).map((t, i) => (
              <span key={i} className="text-[10px] font-semibold bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{t}</span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center">
        <button
          onClick={onView}
          className="text-[11px] font-semibold text-[#0d9488] hover:text-white hover:bg-[#34A853] border border-[#34A853]/30 hover:border-[#34A853] px-2.5 py-1 rounded-md flex items-center gap-1 transition-all"
          data-testid={`portfolio-topic-view-${topic.id}`}
        >
          <Eye className="w-3 h-3" /> View
        </button>
      </div>
    </div>
  );
}

/* Topic preview — framework renderer mirroring teacher's TopicPreviewModal */
function PortfolioTopicPreview({ A, topic, loading, onClose }) {
  const date = topic?.topic_date ? new Date(topic.topic_date) : null;
  const sections = topic?.framework?.sections || [];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="portfolio-topic-preview">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 bg-gradient-to-br from-[#34A853]/5 via-white to-[#06B6D4]/5 flex items-start justify-between gap-3 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#34A853] mb-0.5">Topic · {date ? date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</p>
            <h3 className="text-lg font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{topic?.title || 'Topic'}</h3>
            {topic?.summary && <p className="text-[12px] text-gray-600 mt-1 line-clamp-2">{topic.summary}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100" data-testid="portfolio-topic-preview-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50/40">
          {loading ? (
            <div className="flex items-center justify-center py-10"><div className="w-6 h-6 border-2 border-gray-200 border-t-slate-500 rounded-full animate-spin" /></div>
          ) : sections.length === 0 ? (
            <p className="text-[12px] text-gray-400 text-center py-10">No framework sections saved for this topic.</p>
          ) : (
            sections.map((sec, i) => (
              <div key={i} className="rounded-xl bg-white border border-zinc-100 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#0d9488] mb-1">{sec.type || 'section'}</p>
                <h4 className="text-[14px] font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{sec.title || 'Untitled section'}</h4>
                {sec.content && <p className="text-[12px] text-gray-700 leading-relaxed whitespace-pre-wrap">{String(sec.content).slice(0, 1200)}{String(sec.content).length > 1200 ? '…' : ''}</p>}
                {(sec.items || []).length > 0 && (
                  <ul className="list-disc list-inside text-[12px] text-gray-700 space-y-0.5 mt-1.5">
                    {(sec.items || []).slice(0, 12).map((it, j) => <li key={j}>{it.title || it.text || (typeof it === 'string' ? it : JSON.stringify(it).slice(0, 140))}</li>)}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>
        <div className="px-6 py-2.5 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <span className="text-[10px] text-gray-500 flex items-center gap-1"><Eye className="w-3 h-3" /> Read-only admin preview</span>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="portfolio-topic-preview-done">Close</Button>
        </div>
      </div>
    </div>
  );
}
